import { StcTransDto } from '../../../../../services/stcTransService';

/**
 * Builds a complete StcTrans update request object from an existing transaction.
 * This helper eliminates the need to manually copy all fields when updating a transaction.
 *
 * @param trans - The existing StcTrans to base the update on
 * @param overrides - Fields to override in the update request
 * @returns Complete update request object
 */
export function buildStcTransUpdateRequest(
    trans: StcTransDto,
    overrides: Partial<{
        quantity: number;
        itemBatchNo: string | undefined;
        itemBatchExpDate: Date | undefined;
        locationName: string;
        lineStatus: number;
    }> = {}
) {
    return {
        batchID: trans.batchID,
        lineType: trans.lineType,
        orderNo: trans.orderNo,
        lineNo: trans.lineNo,
        locationName: overrides.locationName ?? trans.locationName,
        quantity: overrides.quantity ?? trans.quantity,
        transDt: trans.transDt,
        warehouse: trans.warehouse,
        toWarehouse: trans.toWarehouse,
        operatorID: trans.operatorID,
        profileID: trans.profileID,
        exportedYesNo: trans.exportedYesNo,
        lineStatus: overrides.lineStatus ?? trans.lineStatus,
        itemNo: trans.itemNo,
        itemUnitCode: trans.itemUnitCode,
        itemBarCode: trans.itemBarCode,
        description1: trans.description1,
        itemBatchNo: overrides.itemBatchNo !== undefined ? overrides.itemBatchNo : trans.itemBatchNo,
        itemBatchExpDate: overrides.itemBatchExpDate !== undefined ? overrides.itemBatchExpDate : trans.itemBatchExpDate,
        itemSerialNo: trans.itemSerialNo,
        erpLinkName: trans.erpLinkName,
        shipmentNo: trans.shipmentNo,
        quantityOrigin: trans.quantityOrigin,
        itemUnitTxt: trans.itemUnitTxt,
        lineTypeOrigin: trans.lineTypeOrigin,
        price: trans.price,
        currency: trans.currency,
        reasonCode: trans.reasonCode,
        transType: trans.transType,
        sscc: trans.sscc,
        quantityCorrected: trans.quantityCorrected,
        freeField1: trans.freeField1,
        freeField2: trans.freeField2,
        freeField3: trans.freeField3,
        appFreeField1: trans.appFreeField1,
        appFreeField2: trans.appFreeField2,
        quantityGross: trans.quantityGross,
        transitPackagingID: trans.transitPackagingID,
        clientID: trans.clientID,
        stcBoxNo: trans.stcBoxNo,
        grShipmentNo: trans.grShipmentNo,
        externalSystemID: trans.externalSystemID,
        externalSystemStatus: trans.externalSystemStatus,
        itemType: trans.itemType,
        orderNoERP: trans.orderNoERP,
        lineNoERP: trans.lineNoERP
    };
}

/**
 * Updates a transaction with fallback to delete/create if PUT is not supported.
 *
 * @param stcTransService - The stcTransService instance
 * @param batchId - The batch ID
 * @param lineId - The line ID to update
 * @param updateRequest - The update request object
 */
export async function updateTransactionWithFallback(
    stcTransService: {
        updateTransaction: (batchId: string, lineId: number, request: any) => Promise<any>;
        deleteTransaction: (batchId: string, lineId: number) => Promise<void>;
        createTransaction: (batchId: string, request: any) => Promise<any>;
    },
    batchId: string,
    lineId: number,
    updateRequest: ReturnType<typeof buildStcTransUpdateRequest>
): Promise<void> {
    try {
        await stcTransService.updateTransaction(batchId, lineId, updateRequest);
        console.log('Transaction updated successfully using PUT method');
    } catch (updateError: any) {
        // Fallback to delete/create if PUT is not supported
        console.log('PUT not supported, falling back to delete/create method');
        await stcTransService.deleteTransaction(batchId, lineId);
        await stcTransService.createTransaction(batchId, updateRequest);
        console.log('Transaction updated using fallback delete/create method');
    }
}
